import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, take, tap } from 'rxjs';
import { UserService } from '../user/user.service';
import { AuthService } from '../auth/auth.service';
import { ACH_DELETE_PAYMENT_ENDPOINT, ACH_SUBMIT_PAYMENT_ENDPOINT, ACH_UPDATE_PAYMENT_ENDPOINT } from 'src/app/constants/api-routes';

/** Object parameter for pending payments */
export interface PendingPaymentData {
    transactionId: number;
    loanNumber: string;
    walletId: string | undefined;
    amount: number;
    paymentDate: Date | null;
}

/** Object response for creating a pending payment (submit ach) */
export interface CreatePendingPaymentResponse {
    payload: {
        data: string;
    };
    status: {
        code: number;
        message: string;
        executionTime: number;
    };
}

@Injectable({
  providedIn: 'root'
})
export class AchService {
    /** Injected services */
    private _userService: UserService = inject(UserService);
    private _authService: AuthService = inject(AuthService);
    private _http: HttpClient = inject(HttpClient);

    constructor() { }

    /** ACH operations */

    /**
     * Submit a new ach
     * 
     * @param paymentData Provided data to construct and supply to backend endpoint
     * @returns 
     */
    public submitAch$(paymentData: PendingPaymentData): Observable<CreatePendingPaymentResponse> {
        // Check if the user is authenticated.
        if (!this._authService.authenticated) {
            console.error('[ACH-SERVICE]: User not authenticated');
            throw new Error('User is not authenticated');
        }

        // Check if the user object exists
        const user = this._userService.user;
        if (!user || !user.id) {
            console.error('[ACH-SERVICE]: User information missing');
            throw new Error('User information is missing');
        }

        const companyId = this._userService.company?.companyId;
        if (!companyId) {
            console.error('[ACH-SERVICE]: Company information missing');
            throw new Error('Company information is missing');
        }

        // Get request parameters
        const userId = user.id;
        const loanNumber = paymentData.loanNumber;
        const achWalletId = paymentData.walletId;

        // Validate request parameters
        if (userId == undefined) {
            throw new Error('Cannot submit payment, missing user id');
        }
        if (loanNumber == undefined) {
            throw new Error('Cannot submit payment, missing loan number');
        }
        if (achWalletId == undefined) {
            throw new Error('Cannot submit payment, missing ACH wallet ID');
        }

        // Build the endpoint URL with the correct parameters
        const endpointUrl = ACH_SUBMIT_PAYMENT_ENDPOINT(companyId, loanNumber, achWalletId);

        // Prepare the request body. Format the payment date as MM/DD/YYYY
        const body = {
            Amount: paymentData.amount,
            BillingStartDate: this.formatDate(paymentData.paymentDate)
        };

        return this._http.post<CreatePendingPaymentResponse>(endpointUrl, body).pipe(
            take(1),
            // TODO: (api spec) create new pending nls transaction from backend response
            tap((response: any) => {
                return;
            }),
        );
    }

    /**
     * Deletes an existing pending payment.
     *
     * @param loanNumber The loan number associated with the pending payment.
     * @param rowId The RowID of the pending payment record.
     * @returns An Observable for the HTTP DELETE request.
     */
    public deletePendingPayment$(loanNumber: string, rowId: string): Observable<any> {
        // Check if the user is authenticated.
        if (!this._authService.authenticated) {
            console.error('[ACH-SERVICE]: User not authenticated');
            throw new Error('User is not authenticated');
        }

        // Retrieve the user information.
        const user = this._userService.user;
        if (!user || !user.id) {
            console.error('[ACH-SERVICE]: User information missing');
            throw new Error('User information is missing');
        }

        const companyId = this._userService.company?.companyId;
        if (!companyId) {
            console.error('[ACH-SERVICE]: Company information missing');
            throw new Error('Company information is missing');
        }

        // Build the endpoint URL using the loanNumber and rowId.
        const endpointUrl = ACH_DELETE_PAYMENT_ENDPOINT(companyId, loanNumber, rowId);

        return this._http.put<any>(endpointUrl, null);
    }

    /**
     * Updates an existing pending payment using the ACH update endpoint.
     *
     * @param loanNumber The loan number associated with the pending payment.
     * @param rowId The RowID of the pending payment record.
     * @param paymentData The payment data object containing updated fields.
     *                     For example, it should have Amount and BillingStartDate at minimum.
     * @returns An Observable for the HTTP PUT request.
     */
    public updatePendingPayment$(loanNumber: string, rowId: string, paymentData: PendingPaymentData): Observable<any> {
        // Check if the user is authenticated.
        if (!this._authService.authenticated) {
            console.error('[ACH-SERVICE]: User not authenticated');
            throw new Error('User is not authenticated');
        }

        // Retrieve the user information.
        const user = this._userService.user;
        if (!user || !user.id) {
            console.error('[ACH-SERVICE]: User information missing');
            throw new Error('User information is missing');
        }

        const companyId = this._userService.company?.companyId;
        if (!companyId) {
            console.error('[ACH-SERVICE]: Company information missing');
            throw new Error('Company information is missing');
        }

        // Build the endpoint URL using loanNumber and rowId.
        const endpointUrl = ACH_UPDATE_PAYMENT_ENDPOINT(companyId, loanNumber, rowId);

        // Create interface for request body
        const body = {
            Amount: paymentData.amount,
            BillingStartDate: this.formatDate(paymentData.paymentDate),
        }

        // Call the backend update endpoint using HTTP PUT.
        return this._http.put<any>(endpointUrl, body);
    }

    /** Helper methods */

    /**
     * Helper function to format a date as MM/DD/YYYY.
     */
    private formatDate(date: Date | null): string {
        if (!date) {
            return '';
        }

        const d = new Date(date);
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const year = d.getFullYear();

        return `${month}/${day}/${year}`;
    }
}
